import React, { useState, useRef } from 'react';
import { View, TouchableOpacity, StyleSheet, Platform, Modal, Dimensions, Keyboard } from 'react-native';
import { ThemedText, ThemedTouchableOpacity } from '@app/theme/Components';
import { ThemedView } from '@app/theme';
import { CaretUp, CaretDown } from '../../sharedComponents/Icons';
import BottomSheetContainer from '../Bottomsheet/Bottomsheet';
import { trackEvent } from '@app/utils/amplitude';

interface DropdownProps {
    options: {
        label: string;
        value: string;
    }[];
    selectedOption: { label: string, value: string };
    onSelect: (option: { label: string, value: string }) => void;
    label: string;
    activeLabel?: string;
    scrollRef?: React.RefObject<any>; // Keep this for web calculations
}

const Dropdown = ({ options, selectedOption, onSelect, label, activeLabel, scrollRef }: DropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<View>(null);
    const [dropdownPosition, setDropdownPosition] = useState<'above' | 'below'>('below');
    const [dropdownWidth, setDropdownWidth] = useState(0);
    const [dropdownPositionY, setDropdownPositionY] = useState(0);

    const { height: screenHeight } = Dimensions.get('window');

    // Check if it's mobile view
    const isMobileView = () => {
        if (Platform.OS === 'web') {
            return Dimensions.get('window').width <= 768;
        }
        return true; // Always mobile for native platforms
    };

    const handleSelect = (option: { label: string, value: string }) => {
        trackEvent('SUBMITBANKDOCS_DOCTYPE_SELECTED', { selectedDoc: option.value })
        onSelect(option);
        setIsOpen(false);
    };

    const handlePress = () => {
        Keyboard.dismiss()
        trackEvent('SUBMITBANKDOCS_DOCTYPEDROPDOWN_CLICKED')
        if (isMobileView()) {
            setIsOpen(true);
        } else {
            // Original web calculation logic
            if (scrollRef?.current && dropdownRef.current) {
                // @ts-ignore
                scrollRef.current.getScrollResponder()?.getScrollableNode().measure((_, __, ___, ____, _____, scrollY) => {
                    const scrollOffset = scrollY * -1; // Convert to positive scroll offset
                    // @ts-ignore
                    dropdownRef?.current?.measure((x, y, width, height, pageX, pageY) => {
                        // Adjust pageY by the scroll position to get true position
                        setDropdownWidth(width);
                        setDropdownPositionY(pageY + height + 4)
                        const truePageY = pageY - scrollOffset;
                        // Calculate visible space below the dropdown
                        const dropdownBottom = screenHeight - (truePageY + height);
                        const dropdownOptionsHeight = Math.min(options.length * 50, 200); // Estimate dropdown height
                        // Check if there's enough space for the dropdown

                        if (dropdownBottom < dropdownOptionsHeight - 60) {
                            setDropdownPositionY((pageY + height + 4) - (dropdownOptionsHeight + 55));
                        }
                    });
                });
            }
            setTimeout(() => {
                setIsOpen(!isOpen);
            }, 10);
        }
    };

    const renderMobileDropdown = () => (
        <BottomSheetContainer
            isVisible={isOpen}
            close={() => setIsOpen(false)}
            showCloseIcon={true}
            closeOnBackdropPress={true}
            borderTopRightRadius={10}
            borderTopLeftRadius={10}
        >
            <ThemedView paddingVertical='lg'>
                <ThemedText paddingHorizontal='lg' variant='h3s' mb='lg' color='text-primary'>Select Option</ThemedText>
                {options.map((option, index) => (
                    <ThemedTouchableOpacity
                        key={index}
                        paddingVertical='md'
                        paddingHorizontal='lg'
                        borderBottomWidth={index !== options.length - 1 ? 1 : 0}
                        borderBottomColor='border-secondary'
                        onPress={() => handleSelect(option)}
                        style={[
                            option?.value === selectedOption?.value && {
                                backgroundColor: '#F1F5F9'
                            }
                        ]}
                    >
                        <ThemedText 
                            color={option?.value === selectedOption?.value ? 'text-primary' : 'text-secondary'} 
                            variant='b2r'
                        >
                            {option?.label}
                        </ThemedText>
                    </ThemedTouchableOpacity>
                ))}
            </ThemedView>
        </BottomSheetContainer>
    );

    const renderDesktopDropdown = () => (
        <Modal
            visible={isOpen}
            transparent={true}
            animationType="fade"
            onRequestClose={() => setIsOpen(false)}
        >
            <TouchableOpacity
                style={styles.modalOverlay}
                activeOpacity={1}
                onPress={() => setIsOpen(false)}
            >
                <ThemedView
                    style={[styles.modalContent, { width: dropdownWidth }]}
                    shadowColor='black'
                    shadowOffset={{ width: 0, height: 2 }}
                    shadowOpacity={0.25}
                    shadowRadius={3.84}
                    elevation={5}
                    position='absolute'
                    {...(dropdownPosition === 'above' ? { bottom: dropdownPositionY, marginBottom: 'xs' } : { top: dropdownPositionY, marginTop: 'xs' })}
                    borderRadius='sm'
                    borderWidth={1}
                    borderColor='border-secondary'
                    backgroundColor='bg-surface-primary'
                    overflow='scroll'
                    maxHeight={200}
                >
                    {options.map((option, index) => (
                        <ThemedTouchableOpacity
                            key={index}
                            paddingVertical='md'
                            paddingHorizontal='lg'
                            borderBottomWidth={index !== options.length - 1 ? 1 : 0}
                            borderBottomColor='border-secondary'
                            style={[
                                option?.value === selectedOption?.value && styles.selectedOption
                            ]}
                            onPress={() => handleSelect(option)}
                        >
                            <ThemedText color='text-secondary' variant='b3r'>{option?.label}</ThemedText>
                        </ThemedTouchableOpacity>
                    ))}
                </ThemedView>
            </TouchableOpacity>
        </Modal>
    );

    return (
        <View pointerEvents='box-none' ref={dropdownRef} style={styles.container}>
            {activeLabel && selectedOption?.label !== '' && (
                <ThemedView 
                    paddingHorizontal='sm' 
                    alignSelf='flex-start' 
                    zIndex={1} 
                    top={10} 
                    left={5} 
                    bg='bg-surface-primary' 
                    flexDirection='row' 
                    alignItems='center' 
                    justifyContent='space-between'
                >
                    <ThemedText color='text-secondary' variant='b4r'>{activeLabel}</ThemedText>
                </ThemedView>
            )}
            
            <ThemedTouchableOpacity
                onPress={handlePress}
                activeOpacity={0.7}
                borderRadius='xs'
                borderWidth={1}
                borderColor={isOpen ? 'border-action' : 'border-form'}
                backgroundColor='bg-surface-primary'
                paddingVertical='md'
                paddingHorizontal='lg'
                flexDirection='row'
                justifyContent='space-between'
                alignItems='center'
            >
                <ThemedText 
                    color={selectedOption && selectedOption?.label !== '' ? 'text-primary' : 'text-placeholder'} 
                    variant='b2r'
                >
                    {selectedOption && selectedOption?.label !== '' ? selectedOption?.label : label}
                </ThemedText>
                {isOpen ? <CaretUp size={16} weight="bold" /> : <CaretDown size={16} weight="bold" />}
            </ThemedTouchableOpacity>

            {/* Conditionally render based on platform */}
            {isMobileView() ? renderMobileDropdown() : renderDesktopDropdown()}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        position: 'relative',
        zIndex: Platform.OS === 'web' ? 'unset' : 1,
    },
    arrow: {
        marginLeft: 8,
        fontSize: 12,
    },
    dropdown: {
        position: 'absolute',
        backgroundColor: 'white',
        borderRadius: 8,
        shadowColor: '#000',
        shadowOffset: {
          width: 0,
          height: 4,
        },
        shadowOpacity: 0.14,
        shadowRadius: 8,
        elevation: 5,
        maxHeight: 200,
    },
    selectedOption: {
        backgroundColor: '#F1F5F9',
    },
    optionText: {
        fontSize: 16,
    },
    modalOverlay: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalContent: {
        width: '80%',
    },
});

export default Dropdown;


